import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { conversationsAPI } from '../utils/api';
import { ConversationSummary, MessageLog } from '../types';
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  ArrowLeftIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';

interface ConversationFilters {
  page: number;
  limit: number;
  channel?: string;
  search?: string;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'ora';
  if (diffMin < 60) return `${diffMin} min fa`;
  if (diffHours < 24) return `${diffHours}h fa`;
  if (diffDays === 1) return 'ieri';
  if (diffDays < 7) return `${diffDays}g fa`;
  return new Intl.DateTimeFormat('it-IT', { dateStyle: 'short' }).format(date);
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return '';
  }
}

function formatContactId(contactId: string, channel: string): string {
  if (channel === 'whatsapp') {
    return contactId.startsWith('+') ? contactId : `+${contactId}`;
  }
  return `Chat #${contactId}`;
}

function truncateText(text: string | null | undefined, maxLength: number = 60): string {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

// Group messages by date for date separators
function groupMessagesByDate(messages: MessageLog[]): { date: string; messages: MessageLog[] }[] {
  const groups: { date: string; messages: MessageLog[] }[] = [];
  let currentDate = '';

  for (const msg of messages) {
    const msgDate = formatDate(msg.created_at);
    if (msgDate !== currentDate) {
      currentDate = msgDate;
      groups.push({ date: msgDate, messages: [msg] });
    } else {
      groups[groups.length - 1].messages.push(msg);
    }
  }
  return groups;
}

const Conversations: React.FC = () => {
  const { user } = useAuth();
  const isHost = user?.is_host || false;

  const [filters, setFilters] = useState<ConversationFilters>({ page: 1, limit: 20 });
  const [selectedConversation, setSelectedConversation] = useState<ConversationSummary | null>(null);
  const [messagePage, setMessagePage] = useState(1);
  const [showThread, setShowThread] = useState(false); // For mobile responsive
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Fetch conversations list
  const { data: conversationsData, isLoading: isLoadingConversations } = useQuery({
    queryKey: ['conversations', filters],
    queryFn: async () => {
      const response = await conversationsAPI.getConversations(filters);
      return response.data;
    },
  });

  const conversations: ConversationSummary[] = conversationsData?.data || [];
  const convPagination = conversationsData?.pagination;

  // Fetch messages for selected conversation
  const { data: messagesData, isLoading: isLoadingMessages } = useQuery({
    queryKey: ['conversation-messages', selectedConversation?.contact_id, selectedConversation?.channel, messagePage],
    queryFn: async () => {
      if (!selectedConversation) return null;
      const response = await conversationsAPI.getConversationMessages(
        selectedConversation.contact_id,
        { channel: selectedConversation.channel, page: messagePage, limit: 50 },
      );
      return response.data;
    },
    enabled: !!selectedConversation,
  });

  const messages: MessageLog[] = messagesData?.data || [];
  const msgPagination = messagesData?.pagination;
  const threadUserName: string | null = messagesData?.user_name || selectedConversation?.user_name || null;

  // Auto-scroll to bottom when messages load
  useEffect(() => {
    if (messages.length > 0 && messagePage === 1) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, messagePage]);

  const handleSelectConversation = (conv: ConversationSummary) => {
    setSelectedConversation(conv);
    setMessagePage(1);
    setShowThread(true);
  };

  const handleFilterChange = (key: keyof ConversationFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value,
    }));
  };

  const clearFilters = () => {
    setFilters({ page: 1, limit: 20 });
  };

  const hasActiveFilters = !!filters.channel || !!filters.search;

  const getChannelBadge = (channel: string, small = false) => {
    const sizeClasses = small ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-xs';
    const iconSize = small ? 'h-2.5 w-2.5' : 'h-3 w-3';
    if (channel === 'telegram') {
      return (
        <span className={`inline-flex items-center rounded-full font-medium bg-blue-100 text-blue-800 ${sizeClasses}`}>
          <ChatBubbleLeftRightIcon className={`${iconSize} mr-0.5`} />
          TG
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center rounded-full font-medium bg-green-100 text-green-800 ${sizeClasses}`}>
        <DevicePhoneMobileIcon className={`${iconSize} mr-0.5`} />
        WA
      </span>
    );
  };

  // --- Conversation List Panel ---
  const renderConversationList = () => (
    <div className="flex flex-col h-full">
      {/* Search & Filters */}
      <div className="p-3 border-b border-gray-200 space-y-2">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cerca contatto o messaggio..."
            value={filters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value || undefined)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filters.channel || ''}
            onChange={(e) => handleFilterChange('channel', e.target.value || undefined)}
            className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">Tutti i canali</option>
            <option value="telegram">Telegram</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="flex items-center text-xs text-gray-500 hover:text-gray-700">
              <XMarkIcon className="h-3.5 w-3.5 mr-0.5" />
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {isLoadingConversations ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <ChatBubbleOvalLeftEllipsisIcon className="h-12 w-12 mb-2" />
            <p className="text-sm">Nessuna conversazione</p>
          </div>
        ) : (
          conversations.map((conv) => {
            const isActive = selectedConversation?.contact_id === conv.contact_id && selectedConversation?.channel === conv.channel;
            return (
              <div
                key={`${conv.contact_id}-${conv.channel}`}
                onClick={() => handleSelectConversation(conv)}
                className={`flex items-start px-3 py-3 cursor-pointer border-b border-gray-100 transition-colors ${
                  isActive
                    ? 'bg-primary-50 border-l-4 border-l-primary-500'
                    : 'hover:bg-gray-50 border-l-4 border-l-transparent'
                }`}
              >
                {/* Avatar / Channel icon */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                  conv.channel === 'whatsapp' ? 'bg-green-100' : 'bg-blue-100'
                }`}>
                  {conv.channel === 'whatsapp' ? (
                    <DevicePhoneMobileIcon className="h-5 w-5 text-green-600" />
                  ) : (
                    <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {conv.user_name || formatContactId(conv.contact_id, conv.channel)}
                      </span>
                      {getChannelBadge(conv.channel, true)}
                    </div>
                    <span className="text-[11px] text-gray-400 flex-shrink-0 ml-2">
                      {formatRelativeTime(conv.last_message_at)}
                    </span>
                  </div>

                  {conv.user_name && (
                    <p className="text-[11px] text-gray-400 truncate">
                      {formatContactId(conv.contact_id, conv.channel)}
                    </p>
                  )}

                  <div className="flex items-center justify-between mt-0.5">
                    <p className="text-xs text-gray-500 truncate flex-1">
                      {conv.last_direction === 'outbound' && (
                        <ArrowUpRightIcon className="h-3 w-3 inline mr-0.5 text-orange-400" />
                      )}
                      {conv.last_direction === 'inbound' && (
                        <ArrowDownLeftIcon className="h-3 w-3 inline mr-0.5 text-blue-400" />
                      )}
                      {truncateText(conv.last_message_text) || <span className="italic text-gray-400">[media]</span>}
                    </p>
                    <span className="flex-shrink-0 ml-2 bg-gray-100 text-gray-600 text-[10px] font-medium px-1.5 py-0.5 rounded-full">
                      {conv.message_count}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {convPagination && convPagination.pages > 1 && (
        <div className="px-3 py-2 border-t border-gray-200 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {convPagination.total} conversazioni
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleFilterChange('page', convPagination.page - 1)}
              disabled={convPagination.page === 1}
              className="p-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <span className="text-xs text-gray-600 px-1">
              {convPagination.page}/{convPagination.pages}
            </span>
            <button
              onClick={() => handleFilterChange('page', convPagination.page + 1)}
              disabled={convPagination.page === convPagination.pages}
              className="p-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // --- Chat Thread Panel ---
  const renderChatThread = () => {
    if (!selectedConversation) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-400">
          <ChatBubbleOvalLeftEllipsisIcon className="h-16 w-16 mb-3" />
          <p className="text-lg font-medium">Seleziona una conversazione</p>
          <p className="text-sm mt-1">Scegli una conversazione dalla lista per vederne i messaggi</p>
        </div>
      );
    }

    const messageGroups = groupMessagesByDate(messages);

    return (
      <div className="flex flex-col h-full">
        {/* Thread Header */}
        <div className="flex items-center px-4 py-3 border-b border-gray-200 bg-white">
          {/* Back button (mobile) */}
          <button
            onClick={() => setShowThread(false)}
            className="lg:hidden mr-3 p-1 rounded hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
          </button>

          <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center mr-3 ${
            selectedConversation.channel === 'whatsapp' ? 'bg-green-100' : 'bg-blue-100'
          }`}>
            {selectedConversation.channel === 'whatsapp' ? (
              <DevicePhoneMobileIcon className="h-4 w-4 text-green-600" />
            ) : (
              <ChatBubbleLeftRightIcon className="h-4 w-4 text-blue-600" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900 truncate">
                {threadUserName || formatContactId(selectedConversation.contact_id, selectedConversation.channel)}
              </span>
              {getChannelBadge(selectedConversation.channel, true)}
            </div>
            {threadUserName && (
              <p className="text-xs text-gray-500">
                {formatContactId(selectedConversation.contact_id, selectedConversation.channel)}
              </p>
            )}
          </div>

          <span className="text-xs text-gray-400">
            {selectedConversation.message_count} messaggi
          </span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 bg-gray-50">
          {isLoadingMessages ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : (
            <>
              {/* Load older messages */}
              {msgPagination && msgPagination.pages > 1 && messagePage < msgPagination.pages && (
                <div className="flex justify-center mb-4">
                  <button
                    onClick={() => setMessagePage((p) => p + 1)}
                    className="text-xs text-primary-600 hover:text-primary-700 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-200"
                  >
                    Carica messaggi precedenti
                  </button>
                </div>
              )}

              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                  <ChatBubbleOvalLeftEllipsisIcon className="h-10 w-10 mb-2" />
                  <p className="text-sm">Nessun messaggio in questa conversazione</p>
                </div>
              ) : (
                messageGroups.map((group) => (
                  <div key={group.date}>
                    {/* Date separator */}
                    <div className="flex items-center justify-center my-4">
                      <span className="bg-white text-xs text-gray-500 px-3 py-1 rounded-full shadow-sm border border-gray-200">
                        {group.date}
                      </span>
                    </div>

                    {/* Messages in this date group */}
                    {group.messages.map((msg) => {
                      const isInbound = msg.direction === 'inbound';

                      return (
                        <div
                          key={msg.id}
                          className={`flex mb-3 ${isInbound ? 'justify-start' : 'justify-end'}`}
                        >
                          <div
                            className={`max-w-[85%] px-3 py-2 ${
                              isInbound
                                ? 'bg-white rounded-lg rounded-bl-sm shadow-sm'
                                : 'bg-primary-600 text-white rounded-lg rounded-br-sm'
                            }`}
                          >
                            {msg.media_type && (
                              <div className={`flex items-center gap-1 text-xs mb-1 ${
                                isInbound ? 'text-gray-400' : 'text-primary-200'
                              }`}>
                                <PhotoIcon className="h-3.5 w-3.5" />
                                <span className="italic">{msg.media_type}</span>
                              </div>
                            )}
                            {msg.message_text && (
                              <p className="text-sm whitespace-pre-wrap">{msg.message_text}</p>
                            )}
                            <p className={`text-[10px] mt-1 text-right ${
                              isInbound ? 'text-gray-400' : 'text-primary-200'
                            }`}>
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Conversazioni</h1>
        <p className="text-gray-600 mt-1">
          {isHost
            ? 'Tutte le conversazioni WhatsApp e Telegram della piattaforma'
            : 'Conversazioni WhatsApp e Telegram del tuo tenant'}
        </p>
      </div>

      {/* Split Panel */}
      <div className="bg-white rounded-lg shadow overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
        <div className="flex h-full">
          {/* Left: Conversation List */}
          <div className={`w-full lg:w-96 lg:border-r border-gray-200 flex-shrink-0 ${
            showThread ? 'hidden lg:flex lg:flex-col' : 'flex flex-col'
          }`}>
            {renderConversationList()}
          </div>

          {/* Right: Chat Thread */}
          <div className={`flex-1 ${
            showThread ? 'flex flex-col' : 'hidden lg:flex lg:flex-col'
          }`}>
            {renderChatThread()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Conversations;
